if (!customElements.get('variant-cards')) {
  class VariantCards extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.formId = this.dataset.formId;
      this.optionCount = parseInt(this.dataset.optionCount, 10) || 1;
      this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';
      this.variants = this.readVariants();

      this.addEventListener('change', this.onChange);
      this.refreshAvailability();
      this.updateStock();
      this.syncFormToSelection();
      this.updateBuyButtonLabel();
    }

    // Nothing is preselected, so until every option is chosen the form must
    // not carry Shopify's default variant: disable it, and the guard script
    // shows "Choose an option" on the button.
    syncFormToSelection() {
      const form = document.getElementById(this.formId);
      const idInput = form && form.querySelector('input[name="id"]');
      if (!idInput) return;
      if (this.selection.includes(null)) {
        idInput.disabled = true;
        return;
      }
      const variant = this.findVariant(this.selection);
      if (!variant || String(idInput.value) === String(variant.id)) return;
      this.setFormVariant(variant.id, variant.available);
      this.renderSection(variant.id);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.abortController) this.abortController.abort();
    }

    readVariants() {
      const node = this.querySelector('[data-variant-cards-data]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('variant-cards: could not parse variant data', error);
        return [];
      }
    }

    // Currently selected value for each option index, e.g. ['La Petite (~95g)', 'Woolwich']
    get selection() {
      const values = [];
      for (let i = 0; i < this.optionCount; i++) {
        const checked = this.querySelector(
          `input[data-option-index="${i}"]:checked`
        );
        values[i] = checked ? checked.value : null;
      }
      return values;
    }

    findVariant(values) {
      return this.variants.find((variant) =>
        values.every((value, i) => value === null || variant.options[i] === value)
      );
    }

    onChange(event) {
      if (!event.target.classList.contains('variant-cards__input')) return;

      this.refreshAvailability();
      this.updateStock();

      if (this.selection.includes(null)) {
        this.syncFormToSelection();
        return;
      }

      const variant = this.findVariant(this.selection);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      // Set the form value first so Add to cart is correct even if the
      // section fetch is slow or fails.
      this.setFormVariant(variant.id, variant.available);
      this.updateUrl(variant.id);
      this.renderSection(variant.id);
    }

    // Show "Only X left" on each market card once every other option (the
    // size) is chosen, as stock is held per size and market. Hidden above 10.
    updateStock() {
      const last = this.optionCount - 1;
      const current = this.selection;

      this.querySelectorAll('.variant-cards__stock').forEach((el) => {
        const input = el.closest('.variant-cards__item').querySelector('.variant-cards__input');
        const probe = current.slice();
        probe[last] = input.value;

        let text = '';
        if (!probe.includes(null) && input.dataset.closed !== 'true') {
          const variant = this.variants.find((v) => probe.every((value, i) => v.options[i] === value));
          if (variant && variant.tracked && variant.available && !variant.closed && variant.qty > 0 && variant.qty <= 10) {
            text = `Only ${variant.qty} left`;
          }
        }

        el.textContent = text;
        el.hidden = !text;
      });
    }

    // Disable any value that has no available variant given the other picks.
    refreshAvailability() {
      const current = this.selection;

      this.querySelectorAll('.variant-cards__input').forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        const probe = current.slice();
        probe[index] = input.value;

        const match = this.variants.find(
          (variant) =>
            probe.every(
              (value, i) => value === null || variant.options[i] === value
            ) && variant.available
        );

        const unavailable = !match;
        input.disabled = unavailable;
        input.closest('.variant-cards__item').classList.toggle(
          'variant-cards__item--unavailable',
          unavailable
        );
      });
    }

    // Swap the buy button wording to match the chosen fulfilment method, so
    // customers aren't told to "pick up" an order they've asked to have
    // delivered. Runs on load and after every section re-render.
    updateBuyButtonLabel() {
      const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!button || button.disabled) return;

      const label = button.querySelector('span:not(.loading-overlay__spinner)');
      if (!label) return;

      const isDelivery = this.selection.some(
        (value) => value && /delivery/i.test(value)
      );

      label.textContent = isDelivery
        ? 'Pre-order for delivery'
        : 'Pre-order for pickup';
    }

    setUnavailable() {
      const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!button) return;
      button.disabled = true;
      const text = button.querySelector('span');
      if (text) text.textContent = 'Unavailable';
    }

    setFormVariant(variantId, available) {
      const form = document.getElementById(this.formId);
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      idInput.value = variantId;
      idInput.disabled = !available;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateUrl(variantId) {
      if (!this.shouldUpdateUrl) return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variantId}`);
    }

    async renderSection(variantId) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.classList.add('variant-cards--loading');

      try {
        const response = await fetch(
          `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`,
          { signal: this.abortController.signal }
        );
        if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        this.swap(doc, `#price-${this.sectionId}`);
        this.swap(doc, `#Sku-${this.sectionId}`);
        this.swap(doc, `#Inventory-${this.sectionId}`);
        this.swap(doc, `#Quantity-Rules-${this.sectionId}`);
        this.swap(doc, `#Volume-${this.sectionId}`);
        this.updateSubmitButton(doc);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.classList.remove('variant-cards--loading');
      }
    }

    swap(sourceDoc, selector) {
      const source = sourceDoc.querySelector(selector);
      const destination = document.querySelector(selector);
      if (!source || !destination) return;

      destination.innerHTML = source.innerHTML;
      destination.className = source.className;
    }

    updateSubmitButton(sourceDoc) {
      const id = `ProductSubmitButton-${this.sectionId}`;
      const source = sourceDoc.getElementById(id);
      const destination = document.getElementById(id);
      if (!source || !destination) return;

      destination.disabled = source.hasAttribute('disabled');

      const sourceText = source.querySelector('span');
      const destinationText = destination.querySelector('span');
      if (sourceText && destinationText) {
        destinationText.textContent = sourceText.textContent;
      }

      // Must run last — the swap above resets the label to Shopify's default.
      this.updateBuyButtonLabel();
    }

    customElementsPlaceholder() {}
  }

  customElements.define('variant-cards', VariantCards);
}